import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Scribbles } from "@somethings-ui/core/assets";
import { FireIcon } from "@heroicons/react/24/outline";

export type HotTakesMode = "write" | "random";

interface HotTakesModePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMode: (mode: HotTakesMode) => void;
}

export function HotTakesModePicker({
  isOpen,
  onClose,
  onSelectMode,
}: HotTakesModePickerProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-md gap-0 overflow-hidden rounded-2xl p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="flex flex-col gap-2 border-b border-zinc-100 px-8 pb-6 pt-8">
          <div className="flex items-center justify-center gap-2">
            <FireIcon
              aria-hidden
              strokeWidth={2}
              className="h-6 w-6 shrink-0 text-zinc-800"
            />
            <DialogTitle className="text-2xl font-bold tracking-tight">
              Hot Takes
            </DialogTitle>
          </div>
          <DialogDescription className="text-center text-sm text-secondary">
            Spark 🔥 conversations with your teen by sending them easy to
            answer agree/disagree questions
          </DialogDescription>
        </div>

        {/* Mode rows */}
        <div className="flex flex-col gap-3 p-6">
          <ModeRow
            illustration="/assets/scribble-orange.png"
            title="Write Your Own"
            subtitle="Create custom hot takes from scratch"
            onClick={() => onSelectMode("write")}
          />
          <ModeRow
            illustration={Scribbles.greenSwoosh}
            title="Get Random Prompts"
            subtitle="Use pre-generated hot takes"
            onClick={() => onSelectMode("random")}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ModeRowProps {
  illustration: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}

function ModeRow({ illustration, title, subtitle, onClick }: ModeRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-24 w-full items-center gap-4 rounded-xl bg-surfaces-z3 px-6 text-left transition-transform",
        "hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-indigo-500",
      )}
    >
      <img
        src={illustration}
        alt=""
        aria-hidden
        className="h-10 w-10 shrink-0 object-contain"
      />
      <div className="flex flex-col gap-1">
        <span className="text-base leading-tight text-primary">{title}</span>
        <span className="text-sm leading-tight text-secondary">{subtitle}</span>
      </div>
    </button>
  );
}
